import React from 'react';
import { TextField as MuiTextField, styled, IconButton } from '@mui/material';
import { SvgIcon } from '@mui/material';

// Modern styled text field with consistent design across the app
const StyledTextField = styled(MuiTextField)(({ theme, multiline }) => ({
  '& .MuiFormLabel-root': {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    transform: 'none',
    position: 'relative',
    marginBottom: theme.spacing(1),
    textAlign: 'left',
    width: '100%',
    display: 'block',
    '&.Mui-focused': {
      color: theme.palette.text.secondary,
    },
    '&.Mui-error': {
      color: theme.palette.error.main,
    },
  },

  // Скрываем стандартные стрелочки
  '& input[type="number"]::-webkit-outer-spin-button, & input[type="number"]::-webkit-inner-spin-button':
    {
      WebkitAppearance: 'none',
      margin: 0,
    },

  '& input[type="number"]': {
    MozAppearance: 'textfield',
  },

  '& .MuiInputBase-root': {
    height: multiline ? 'auto' : '48px',
    borderRadius: '12px',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.02)',
    transition: theme.transitions.create([
      'border-color',
      'background-color',
      'box-shadow',
    ]),

    '&:hover:not(.Mui-disabled)': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.08)'
          : 'rgba(0, 0, 0, 0.04)',
    },

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
      legend: {
        display: 'none',
      },
    },

    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '1px',
      },
    },

    // Style for the end adornment (icons, buttons)
    '& .MuiInputAdornment-root': {
      marginRight: theme.spacing(1),
      height: '100%', // Make adornment take full height
      maxHeight: 'none', // Remove max-height restriction
    },

    // Consolidated input styles with high specificity
    '& .MuiOutlinedInput-root .MuiOutlinedInput-input': {
      padding: multiline ? '0' : '0 16px',
      height: multiline ? 'auto' : '48px',
      margin: 0,
      textOverflow: 'ellipsis',
      whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
      overflow: 'hidden',
      '&::placeholder': {
        opacity: 0.7,
        textOverflow: 'ellipsis',
        whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
        overflow: 'hidden',
      },
    },
  },

  // Remove any fieldset padding
  '& .MuiOutlinedInput-notchedOutline': {
    top: 0,
  },

  // Disabled state
  '& .Mui-disabled': {
    opacity: 0.7,
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },

  // Error state
  '& .Mui-error': {
    marginLeft: 0,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.error.main,
    },
  },

  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus':
    {
      transition: 'background-color 5000s ease-in-out 0s',
      WebkitTextFillColor: theme.palette.text.primary,
      WebkitBoxShadow: 'none',
      backgroundColor: 'transparent !important',
    },

  '& input:-internal-autofill-selected': {
    backgroundImage: 'none !important',
    backgroundColor: 'transparent !important',
    color: `${theme.palette.text.primary} !important`,
    height: '48px !important',
    boxSizing: 'border-box !important',
  },

  // Убираем горизонтальные марджины у helperText
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    marginRight: 0,
  },
}));

const TriangleUp = (props) => (
  <SvgIcon {...props} viewBox="0 0 8 8">
    <path d="M4 2L7 6H1L4 2Z" />
  </SvgIcon>
);

const TriangleDown = (props) => (
  <SvgIcon {...props} viewBox="0 0 8 8">
    <path d="M4 6L1 2H7L4 6Z" />
  </SvgIcon>
);

const NumberArrowButton = styled(IconButton)(({ theme }) => ({
  padding: 0,
  minWidth: '14px',
  width: '14px',
  height: '12px',
  color: theme.palette.text.primary,
  opacity: 0.7,
  '&:hover': {
    opacity: 1,
    backgroundColor: 'transparent',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '12px', // Уменьшаем размер треугольников
  },
}));

const NumberArrowsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  marginRight: 0,
  gap: 0,
});

const TextField = React.forwardRef(
  (
    { label, InputProps, multiline, rows = 1, type, onChange, value, ...props },
    ref
  ) => {
    // Получаем min из InputProps или по умолчанию 1
    const min =
      type === 'number'
        ? InputProps?.inputProps?.min !== undefined
          ? Number(InputProps.inputProps.min)
          : 1
        : undefined;

    // Обёртка для onChange: всегда отдаём валидное число
    const handleNumberChange = (e) => {
      let val = parseInt(e.target.value, 10);
      if (isNaN(val) || (min !== undefined && val < min)) val = min;
      // Передаём все оригинальные поля target (включая name), только меняем value
      if (onChange) onChange({ target: { name: props.name, value: val } });
    };

    const handleStep = (step) => (e) => {
      e.preventDefault();
      let currentValue = parseFloat(value);
      if (isNaN(currentValue)) currentValue = min;
      let newValue = currentValue + step;
      if (min !== undefined && newValue < min) newValue = min;
      const max =
        InputProps?.inputProps?.max !== undefined
          ? Number(InputProps.inputProps.max)
          : undefined;
      if (max !== undefined && newValue > max) newValue = max;
      // Для стрелочек явно прокидываем name
      if (onChange) onChange({ target: { name: props.name, value: newValue } });
    };

    const getNumberInputProps = () => {
      if (type !== 'number') return InputProps;

      return {
        ...InputProps,
        endAdornment: (
          <NumberArrowsContainer>
            <NumberArrowButton size="small" onClick={handleStep(1)}>
              <TriangleUp />
            </NumberArrowButton>
            <NumberArrowButton size="small" onClick={handleStep(-1)}>
              <TriangleDown />
            </NumberArrowButton>
          </NumberArrowsContainer>
        ),
      };
    };

    return (
      <StyledTextField
        variant="outlined"
        label={label}
        multiline={multiline}
        rows={rows}
        type={type}
        value={value}
        onChange={type === 'number' ? handleNumberChange : onChange}
        InputLabelProps={{
          shrink: true,
        }}
        InputProps={getNumberInputProps()}
        {...props}
        ref={ref}
      />
    );
  }
);

TextField.displayName = 'TextField';

export default TextField;
